import { getInvites } from 'api/invites.api';
import { getRoles } from 'api/roles.api';
import { getOrgTeams } from 'api/teams.api';
import { getOrgUsers } from 'api/users.api';
import { $detail, $list, $user, $view } from 'signals/global.signals';
import displayError from 'utils/displayError';
import { createUserOptions, filterActiveUsers } from 'utils/userFilters';

export const fetchAndSetFounderCompanyOptionsAndDetail = async () => {
  try {
    const [{ data: userData }, { data: roleData }] = await Promise.all([
      getOrgUsers(),
      getRoles({ userType: $user.value.userType }),
    ]);

    // Filter out archived users for options
    const userOptions = createUserOptions(userData);

    const roleOptions = roleData.map((role) => ({
      label: role.description,
      value: role.id,
    }));

    $view.update({
      userOptions,
      roleOptions,
    });

    $detail.update({
      organizationName: $user.value.organization?.name || '',
      logo: $user.value.organization?.logoImage || '',
      logoColors: $user.value.organization?.logoColors || [],
    });
  } catch (error) {
    displayError({
      message: 'Failed to load team options. Please refresh the page.',
      title: 'Loading Error',
    });
  }
};

export const fetchAndSetFounderTeamData = async () => {
  $view.update({
    isTableLoading: true,
  });

  try {
    const { data } = await getOrgTeams($user.value.organization.id);

    $detail.update({
      logo: data.logo,
      organizationName: data.name,
      logoColors: data.logoColors,
    });

    $list.update({ data });
  } catch (error) {
    displayError({
      message: 'Failed to load team data. Please refresh the page.',
      title: 'Loading Error',
    });
  } finally {
    $view.update({
      isTableLoading: false,
    });
  }
};

export const fetchAndSetOrganizationUsers = async () => {
  $view.update({
    isLoading: true,
    error: null,
    isTableLoading: true,
  });
  $list.reset();

  try {
    const [{ data: userData }, { data: teamData }] = await Promise.all([
      getOrgUsers($user.value.organization.id),
      getOrgTeams($user.value.organization.id),
    ]);

    // Filter out archived users before processing
    const activeUsers = filterActiveUsers(userData);

    activeUsers.forEach((user, userIdx) => {
      activeUsers[userIdx].teams = teamData
        .filter((team) => team.users.find((teamUser) => teamUser.id === user.id))
        .map((team) => team.id);
    });

    $list.update({ data: activeUsers });
  } catch (error) {
    displayError({
      message: 'Failed to load organization users. Please refresh the page.',
      title: 'Loading Error',
    });
  } finally {
    $view.update({
      isLoading: false,
      isTableLoading: false,
    });
  }
};

export const fetchAndSetFounderInvites = async () => {
  $view.update({
    isTableLoading: true,
  });

  try {
    const { data } = await getInvites();

    $list.update({ data });
  } catch (error) {
    displayError({
      message: 'Failed to load invitations. Please refresh the page.',
      title: 'Loading Error',
    });
  } finally {
    $view.update({
      isTableLoading: false,
    });
  }
};
